import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/cn'

/**
 * Yengil dropdown. Portal orqali chiqadi — jadval `overflow: hidden` ichida
 * kesilib qolmasligi uchun.
 */
export function Menu({
  trigger,
  children,
  align = 'start',
  width = 200,
}: {
  trigger: (props: { open: boolean; toggle: () => void; ref: React.Ref<HTMLButtonElement> }) => ReactNode
  children: ReactNode | ((close: () => void) => ReactNode)
  align?: 'start' | 'end'
  width?: number
}) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return
    const r = triggerRef.current.getBoundingClientRect()
    let left = align === 'end' ? r.right - width : r.left
    // Ekran chetidan chiqib ketmasin.
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8))
    setPos({ top: r.bottom + 4, left })
  }, [open, align, width])

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      const t = e.target as Node
      if (menuRef.current?.contains(t) || triggerRef.current?.contains(t)) return
      setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    // Skroll/resize'da menyu tirgakdan uzilib qolmasin.
    const onScroll = () => setOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey, true)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey, true)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    }
  }, [open])

  const close = () => setOpen(false)

  return (
    <>
      {trigger({ open, toggle: () => setOpen((v) => !v), ref: triggerRef })}
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="fixed z-50 rounded-md border border-border-default bg-surface-2 py-1"
            style={{ top: pos.top, left: pos.left, width, boxShadow: 'var(--shadow-popover)' }}
          >
            {typeof children === 'function' ? children(close) : children}
          </div>,
          document.body,
        )}
    </>
  )
}

export function MenuItem({
  children,
  onSelect,
  danger,
  disabled,
  icon,
  trailing,
}: {
  children: ReactNode
  onSelect?: () => void
  danger?: boolean
  disabled?: boolean
  icon?: ReactNode
  trailing?: ReactNode
}) {
  return (
    <button
      role="menuitem"
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm',
        'text-primary hover:bg-surface-3 disabled:opacity-40 disabled:pointer-events-none',
        danger && 'text-danger hover:bg-danger-subtle',
      )}
    >
      {icon && <span className="shrink-0 text-tertiary">{icon}</span>}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {trailing && <span className="shrink-0 text-xs text-tertiary">{trailing}</span>}
    </button>
  )
}

export function MenuSeparator() {
  return <div className="my-1 h-px bg-border-subtle" />
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className="px-2.5 py-1 text-xs font-medium text-tertiary uppercase">{children}</div>
}
